import { useEffect, useState } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { t } from '@/lib/i18n';
import { ApiError } from '@/lib/api';
import { useRecordMemory } from '../hooks';
import type { ChatMessage, Member } from '../schemas';
import styles from './RememberDialog.module.css';

interface RememberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member;
  chatMessage: ChatMessage;
  onSaved?: () => void;
}

export function RememberDialog({
  open,
  onOpenChange,
  member,
  chatMessage,
  onSaved,
}: RememberDialogProps) {
  const [text, setText] = useState(chatMessage.content);
  const [error, setError] = useState<string | null>(null);
  const record = useRecordMemory(member.id);

  useEffect(() => {
    if (open) {
      setText(chatMessage.content);
      setError(null);
    }
  }, [open, chatMessage.content]);

  const trimmed = text.trim();
  const disabled = !trimmed || record.isPending;

  function handleSubmit() {
    if (disabled) return;
    setError(null);
    record.mutate(
      { content: trimmed, source: { kind: 'chat_pin', id: chatMessage.id } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSaved?.();
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.message);
          } else {
            setError(t('Failed to save. Try again.'));
          }
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('Remember this')}
      description={t('{name} will be able to recall this in future chats.', {
        name: member.first_name,
      })}
      actions={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={record.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={disabled}
            isLoading={record.isPending}
          >
            {t('Remember')}
          </Button>
        </>
      }
    >
      <textarea
        className={styles.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        aria-label={t('Memory content')}
      />
      {error ? <Alert variant="error">{error}</Alert> : null}
    </Modal>
  );
}
